import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import DatePicker from '../../common/DatePicker';
import InputWrapper from '../../common/InputWrapper';
import Select from '../../common/Select';
import Button from '../../common/Button';

import { preventSubmittingByEnter, statusOptions } from '../../../helpers';
import { showModal } from '../../../actions/modal';
import { useHistory } from 'react-router-dom';
import { useAutoTranslation } from '../../../helpers/useAutoTranslation';

const FormWrapper = styled('form')`
  position: relative;

  .close-button {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    top: 0;
    right: 0;
    cursor: pointer;
    z-index: 1;

    .fe-x {
      display: block;
      font-size: 16px;
    }
  }

  .dates-block {
    display: flex;

    .react-datepicker-wrapper {
      width: calc((100% - 25px) / 2);
    }

    .date-separator {
      width: 25px;
      text-align: center;
      font-size: 22px;
      font-weight: 600;
    }
  }

  .react-datepicker {
    border-color: #ced4da;
    font-family: inherit;
    color: #323a46;

    &__header {
      background: #f1f5f7;
      border-color: #ced4da;
    }

    &__day {
      color: inherit;
    }

    &__day-name {
      color: inherit;
    }

    &__day--keyboard-selected {
      color: white;
    }

    &__day--selected {
      color: white;
    }

    &__current-month {
      color: inherit;
    }
  }

  .select-wrapper {
    width: 100%;
    max-width: calc(100% - 56px);
  }
`;

const formikInitialValues = {
  dateFrom: null,
  dateTo: null,
  delivery: null,
  status: null,
  city: '',
  product: '',
  article: '',
  barcode: '',
  phone: '',
  trackingNumber: '',
  promocode: '',
  tags: '',
  email: '',
  externalId: '',
};

const FilterBlock = (props) => {
  const { handleCloseFilter, handleSetFilters, filterFormInitial, loading, setFilterFormInitial } =
    props;

  const { t } = useAutoTranslation();

  const dispatch = useDispatch();
  const history = useHistory();

  const formik = useFormik({
    initialValues: filterFormInitial ? filterFormInitial : formikInitialValues,
    onSubmit: (values) => {
      const filters = {};
      if (values.dateFrom) {
        filters.date_from = moment(values.dateFrom).format('YYYY-MM-DD[T]HH:mm:ss');
      }
      if (values.dateTo) {
        filters.date_to = moment(values.dateTo).format('YYYY-MM-DD[T]HH:mm:ss');
      }
      if (values.status && values.status.length) {
        filters.filter_states = values.status.map((s) => s.value).join(',');
      }
      // if(values.delivery && values.delivery.length){
      //     filters.delivery_code = values.delivery.map(s => s.value).join(',');
      // }
      if (values.delivery && values.delivery.length) {
        filters.delivery_code = values.delivery.map((s) => s.value).join(',');
      }
      if (values.city) {
        filters.city = values.city;
      }
      if (values.product) {
        filters.variant_id = values.product;
      }
      if (values.article) {
        filters.product_article = values.article;
      }
      if (values.barcode) {
        filters.product_barcode = values.barcode;
      }
      if (values.phone) {
        filters.filter_phone = values.phone;
      }
      if (values.trackingNumber) {
        filters.filter_tracking_number = values.trackingNumber;
      }
      if (values.promocode) {
        filters.filter_promocode = values.promocode;
      }
      if (values.tags) {
        filters.urlfilter_tags = values.tags;
      }
      if (values.email) {
        filters.filter_email = values.email;
      }
      if (values.externalId) {
        filters.filter_externalId = values.externalId;
      }
      handleSetFilters(filters);
    },
  });
  const { values, errors, submitCount, handleChange, handleSubmit, setFieldValue } = formik;

  useEffect(() => {
    setFilterFormInitial(null);
  }, []);

  const setDateFrom = (date) => {
    setFieldValue('dateFrom', date);
    setFieldValue('dateTo', null);
  };

  const setDateTo = (date) => {
    setFieldValue(
      'dateTo',
      moment(date).set('hour', 23).set('minute', 59).set('second', 59).toDate(),
    );
  };

  const { deliveryInfo } = useSelector((state) => state.orders);

  const deliveryOptions = Array.from(
    deliveryInfo
      .map((item) => {
        let label = '';

        if (item.fbmType && item.title) {
          label = `FBM ${item.fbmType} - поставка на маркетплейс (${item.title})`;
        } else if (item.type === 'SELF_PICKUP') {
          label = `${item.courierTitle} (${item.title})`;
        } else {
          label =
            (item.courierTitle ? item.courierTitle : ``) +
            (item.product ? `: ${item.product}` : ``);
        }

        if (item.title && !item.fbmType) label += ` (${item.title})`;

        return {
          value: item.code,
          label,
        };
      })
      .sort((a, b) => (a.label > b.label ? 1 : -1)),
  );

  const handleDeliveryModal = () => {
    dispatch(showModal('EditDeliveryService', { deliveryInfo, submitCallback: handleSubmitModal }));
  };

  const handleSubmitModal = (arr) => {
    setFieldValue('delivery', arr);
  };

  useEffect(() => {
    formik.resetForm({
      values: {
        delivery: [],
        status: [],
      },
    });
  }, []);

  return (
    <div className="row mb-2">
      <div className="col-12">
        <FormWrapper
          className="alert alert-light"
          onKeyDown={preventSubmittingByEnter}
          onSubmit={handleSubmit}
        >
          <div className="close-button" onClick={handleCloseFilter}>
            <i className="fe-x" />
          </div>
          <div className="row">
            <div className="col-sm-3 mb-2">
              <label>{t('Дата')}</label>
              <div className="dates-block">
                <DatePicker selected={values.dateFrom} onChange={setDateFrom} />
                <div className="date-separator">-</div>
                <DatePicker
                  selected={values.dateTo}
                  minDate={values.dateFrom}
                  maxDate={new Date()}
                  onChange={setDateTo}
                />
              </div>
            </div>
            <div className="col-sm-3 mb-2">
              <Select
                isSearchable
                label={t('Статус заказа')}
                options={statusOptions}
                isMulti
                value={values.status}
                onChange={(e) => setFieldValue('status', e)}
              />
            </div>
            <div className="col-sm-3 mb-2">
              <InputWrapper
                type="text"
                label={t('Телефон')}
                id="phone"
                value={values.phone}
                onChange={handleChange}
              />
            </div>
            <div className="col-sm-3 mb-2">
              <InputWrapper
                type="text"
                label={t('Email')}
                id="email"
                value={values.email}
                error={errors.email}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-sm-3 mb-2">
              <InputWrapper
                type="text"
                label={t('Трекинг номер')}
                id="trackingNumber"
                value={values.trackingNumber}
                onChange={handleChange}
              />
            </div>
            <div
              className="col-sm-3 mb-2"
              style={{ display: 'flex', alignItems: 'flex-end', columnGap: '12px' }}
            >
              <div className="select-wrapper">
                <Select
                  isSearchable
                  label={t('Служба доставки')}
                  options={deliveryOptions}
                  isMulti
                  id="delivery"
                  value={values.delivery}
                  error={errors.delivery}
                  onChange={(e) => setFieldValue('delivery', e)}
                />
              </div>

              <Button
                icon="fe-sliders"
                title={t('Раскрыть расширенный список')}
                onClick={handleDeliveryModal}
                style={{ marginBottom: '1px' }}
              />
            </div>
            <div className="col-sm-3 mb-2">
              <InputWrapper
                type="text"
                label={t('Населенный пункт')}
                placeholder={t('Город, регион')}
                id="city"
                value={values.city}
                error={errors.city}
                onChange={handleChange}
              />
            </div>
            <div className="col-sm-3 mb-2">
              <InputWrapper
                type="text"
                label={t('Промокод')}
                placeholder={t('Промокод')}
                id="promocode"
                value={values.promocode}
                error={errors.promocode}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-sm-3 mb-2">
              <InputWrapper
                type="text"
                label={t('ID товара')}
                id="product"
                value={values.product}
                error={errors.product}
                onChange={handleChange}
              />
            </div>
            <div className="col-sm-3 mb-2">
              <InputWrapper
                type="text"
                label={t(`Артикул товара`)}
                id="article"
                value={values.article}
                error={errors.article}
                onChange={handleChange}
              />
            </div>
            <div className="col-sm-3 mb-2">
              <InputWrapper
                type="text"
                label={t('Штрихкод товара')}
                id="barcode"
                value={values.barcode}
                error={errors.barcode}
                onChange={handleChange}
              />
            </div>
            <div className="col-sm-3 mb-2">
              <InputWrapper
                type="text"
                label={t('Внешний код')}
                id="externalId"
                value={values.externalId}
                error={errors.externalId}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-12">
              <Button type="submit" text={t('Найти')} loading={loading} />
              <Button
                type="reset"
                text={t('Сбросить фильтры')}
                style={{ marginLeft: '12px' }}
                onClick={() =>
                  formik.resetForm({
                    values: {
                      delivery: [],
                      status: [],
                    },
                  })
                }
              />
            </div>
          </div>
        </FormWrapper>
      </div>
    </div>
  );
};

export default FilterBlock;
